import React from 'react';
import { Check, X, ArrowRight, CreditCard, TrendingDown, Percent, Clock, Landmark, Wallet, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета рефинансирования
 */
const ResultsOverview = ({ results, comparisonData }) => {
  if (!results) return null;

  // Определяем, выгодно ли рефинансирование
  const isEffective = results.isRefinancingEffective;
  
  // Получаем информацию о ROI (сроке окупаемости)
  const roiMonths = results.roiMonths || 0;
  const hasRoi = isFinite(roiMonths) && roiMonths > 0;

  return (
    <ToolResult title="Результаты расчета рефинансирования">
      {/* Общий результат (зеленый - выгодно, красный - невыгодно) */}
      <div className={`
        rounded-lg p-5 border shadow-sm mb-6 flex items-center
        ${isEffective 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800/50' 
          : 'bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 border-red-100 dark:border-red-800/50'}
      `}>
        <div className={`
          rounded-full p-3 mr-4
          ${isEffective 
            ? 'bg-green-100 dark:bg-green-800/50 text-green-600 dark:text-green-400' 
            : 'bg-red-100 dark:bg-red-800/50 text-red-600 dark:text-red-400'}
        `}>
          {isEffective ? <Check className="w-8 h-8" /> : <X className="w-8 h-8" />}
        </div>
        <div>
          <h3 className={`text-lg font-semibold mb-1 ${isEffective ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {isEffective 
              ? 'Рефинансирование выгодно!' 
              : 'Рефинансирование невыгодно'}
          </h3>
          <p className={`text-sm ${isEffective ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {isEffective 
              ? hasRoi
                ? `Вы сэкономите ${formatCurrency(results.totalSavings)}. Затраты на рефинансирование окупятся через ${roiMonths} месяцев.`
                : `Вы сэкономите ${formatCurrency(results.totalSavings)} за весь срок кредита.`
              : `Затраты на рефинансирование ${formatCurrency(results.totalFees || 0)} превышают потенциальную экономию.`}
          </p>
        </div>
      </div>

      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Экономия на ежемесячном платеже */}
        <div className={`
          rounded-lg p-5 border shadow-sm
          ${results.paymentDifference > 0 
            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-100 dark:border-blue-800/50' 
            : 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/30 dark:to-slate-900/30 border-gray-200 dark:border-gray-700/50'}
        `}>
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <TrendingDown className="w-4 h-4"/>
            Экономия на ежемесячном платеже
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`
              text-3xl font-bold
              ${results.paymentDifference > 0 
                ? 'text-indigo-800 dark:text-indigo-200' 
                : 'text-gray-500 dark:text-gray-400'}
            `} aria-live="polite">
              {results.paymentDifference > 0 
                ? formatCurrency(results.paymentDifference) 
                : formatCurrency(0)}
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              {results.paymentDifference > 0 
                ? 'в месяц' 
                : 'нет экономии'}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {results.paymentDifference > 0 
              ? `Платеж снизится с ${formatCurrency(results.currentMonthlyPayment)} до ${formatCurrency(results.newMonthlyPayment)}`
              : 'Новый платеж будет выше или равен текущему'}
          </p>
        </div>

        {/* Общая экономия */}
        <div className={`
          rounded-lg p-5 border shadow-sm
          ${results.totalSavings > 0 
            ? 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border-emerald-100 dark:border-emerald-800/50' 
            : 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/30 dark:to-slate-900/30 border-gray-200 dark:border-gray-700/50'}
        `}>
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Wallet className="w-4 h-4"/>
            Общая экономия за весь срок
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`
              text-3xl font-bold
              ${results.totalSavings > 0 
                ? 'text-emerald-800 dark:text-emerald-200' 
                : 'text-gray-500 dark:text-gray-400'}
            `} aria-live="polite">
              {results.totalSavings > 0 
                ? formatCurrency(results.totalSavings) 
                : formatCurrency(0)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {results.totalSavings > 0 
              ? `Включая экономию на процентах ${formatCurrency(results.currentTotalInterest - results.newTotalInterest)}`
              : 'Рефинансирование не приведет к экономии'}
          </p>
        </div>
      </div>

      {/* Сравнение текущего и нового кредита */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6 text-sm">
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-gray-500" />
            Текущий кредит
          </h4>
          <div className="space-y-2 text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Остаток долга:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.currentLoanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span>Ставка:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.currentInterestRate, 2)}%</span>
            </div>
            <div className="flex justify-between">
              <span>Ежемесячный платеж:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.currentMonthlyPayment)}</span>
            </div>
            <div className="flex justify-between">
              <span>Оставшийся срок:</span>
              <span className="font-medium text-gray-900 dark:text-white">{results.currentRemainingTermMonths} мес.</span>
            </div>
            <div className="flex justify-between">
              <span>Общая сумма платежей:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.currentTotalPayments)}</span>
            </div>
            <div className="flex justify-between">
              <span>Переплата по процентам:</span>
              <span className="font-medium text-red-600 dark:text-red-400">{formatCurrency(results.currentTotalInterest)}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <Landmark className="w-4 h-4 text-blue-500" />
            Новый кредит (рефинансирование)
          </h4>
          <div className="space-y-2 text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Сумма кредита:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.newLoanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span>Ставка:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.newInterestRate, 2)}%</span>
            </div>
            <div className="flex justify-between">
              <span>Ежемесячный платеж:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.newMonthlyPayment)}</span>
            </div>
            <div className="flex justify-between">
              <span>Срок кредита:</span>
              <span className="font-medium text-gray-900 dark:text-white">{results.newLoanTermMonths} мес.</span>
            </div>
            <div className="flex justify-between">
              <span>Общая сумма платежей:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.newTotalPayments)}</span>
            </div>
            <div className="flex justify-between">
              <span>Переплата по процентам:</span>
              <span className="font-medium text-red-600 dark:text-red-400">{formatCurrency(results.newTotalInterest)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Разница в ежемесячном платеже" 
          value={formatCurrency(results.paymentDifference)} 
          color={results.paymentDifference > 0 ? "green" : "red"} 
          icon={<TrendingDown className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Экономия на процентах" 
          value={formatCurrency(results.currentTotalInterest - results.newTotalInterest)} 
          color="green" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Снижение срока кредита" 
          value={results.termReduction ? `${results.currentRemainingTermMonths - results.newLoanTermMonths} мес.` : "Срок не уменьшился"} 
          color={results.termReduction ? "green" : undefined}
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Дополнительная сумма к рефинансированию" 
          value={results.additionalAmount > 0 ? formatCurrency(results.additionalAmount) : "Без дополнительных средств"} 
          icon={<CreditCard className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Комиссии и сборы" 
          value={formatCurrency(results.totalFees)} 
          color="orange" 
        />
        
        <ResultItem 
          label="Срок окупаемости затрат" 
          value={hasRoi ? `${roiMonths} месяцев` : "Не окупается"} 
          color={hasRoi ? "green" : "red"} 
        />
        
        <ResultItem 
          label="Банк рефинансирования" 
          value={results.bank ? results.bank.name : "Не выбран"} 
          icon={<Landmark className="w-4 h-4"/>} 
        />

        {/* Предупреждение о комиссиях */}
        {results.totalFees > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mt-2 border border-amber-200 dark:border-amber-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Внимание! Учтите все комиссии</h4>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Общая стоимость рефинансирования составляет {formatCurrency(results.totalFees)}. Уточните актуальные комиссии в выбранном банке перед оформлением.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;